import {
  addWeeks,
  DateRange,
  DateRangeInput,
  DATE_FORMATTER_STR,
  formatDate,
  NotApplicable,
  Select,
  isBefore,
  ScrollContainer,
  Loader,
  ValueOpt,
  StatsTeam,
  Filters,
  useBreakpoints,
  Header,
} from "best-common-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Game from "../components/requests/card/Game";
import SeasonSelect from "../components/requests/card/SeasonSelect";
import { useAuth } from "../contexts/AuthContext";
import { useDropdowns } from "../contexts/DropdownsContext";
import { useMetadata } from "../contexts/MetadataContext";
import { UserAllotmentCountDTO } from "../types/Allotment";
import { GameDTO, GameRequestCountMap } from "../types/Game";
import { UserDTO } from "../types/User";
import { useUrlParams } from "../util/RouteUtil";
import { seasonTypesCodes, SPRING_TRAINING } from "../util/SeasonUtil";
import { getTeamOption } from "../util/TeamUtil";
import { useQuery } from "@tanstack/react-query";
import { queries } from "../queries";

const today = new Date();
const nextWeek = addWeeks(today, 1);
const todayStr = formatDate(today, DATE_FORMATTER_STR);
const nextWeekStr = formatDate(nextWeek, DATE_FORMATTER_STR);

const Home = () => {
  const { userInfo } = useAuth();
  const { teamsOptions } = useDropdowns();
  const { activeSeason } = useMetadata();
  const { isMobile } = useBreakpoints();
  const [_allotments, setAllotments] = useState<UserAllotmentCountDTO[]>([]);
  const [selectedSeason, setSelectedSeason] = useState(seasonTypesCodes[SPRING_TRAINING]);

  const [{ startDate, endDate, homeTeamId, opponentTeamId }, setParam] = useUrlParams({
    startDate: todayStr,
    endDate: nextWeekStr,
    homeTeamId: undefined,
    opponentTeamId: undefined,
  });

  const dateRange: DateRange<Date> = useMemo(
    () => ({
      start: new Date(Date.parse(startDate!)),
      end: new Date(Date.parse(endDate!)),
    }),
    [startDate, endDate],
  );

  const selectedHomeTeam = useMemo(() => getTeamOption(homeTeamId, teamsOptions), [homeTeamId, teamsOptions]);
  const selectedOpponent = useMemo(() => getTeamOption(opponentTeamId, teamsOptions), [opponentTeamId, teamsOptions]);
  const hasDateLimit: boolean = useMemo(() => !opponentTeamId && !homeTeamId, [opponentTeamId, homeTeamId]);

  const updateDate = useCallback(
    (newRange: DateRange<Date>) => {
      setParam({
        startDate: formatDate(newRange.start, DATE_FORMATTER_STR),
        endDate: formatDate(newRange.end, DATE_FORMATTER_STR),
      });
    },
    [setParam],
  );
  const { data: user } = useQuery<UserDTO>({
    ...queries.user.getUser(userInfo!.employeeUserId!),
    enabled: !!userInfo?.employeeUserId,
  });

  const { data: schedule = [], isLoading: isScheduleLoading } = useQuery<GameDTO[]>({
    ...queries.schedule.getSchedule(startDate!, endDate!, homeTeamId ?? "", opponentTeamId ?? ""),
    enabled: isBefore(startDate!, endDate!),
  });

  const { data: gameRequestCounts = {} } = useQuery<GameRequestCountMap>({
    ...queries.gameRequestCounts.getGameRequestCounts(schedule.map((game) => game.gamePk)),
    enabled: schedule.length > 0,
  });

  useEffect(() => {
    if (activeSeason.seasonType) {
      setSelectedSeason(seasonTypesCodes[activeSeason.seasonType.seasonTypeName]);
    }
  }, [activeSeason]);

  return (
    <div>
      <Header title="Request Tickets" flexColumn>
        <Filters
          numberDisplayed={isMobile ? 2 : 4}
          showExpansionButtons={false}
          startsOpen={true}
          indexesToDisplay={isMobile ? [2, 0] : undefined}
          actionIconPosition="absolute"
        >
          <Select
            id="hometeam-dropdown"
            label="Home Team"
            options={teamsOptions}
            value={selectedHomeTeam}
            onChange={(team: ValueOpt<StatsTeam>) =>
              setParam("homeTeamId", team.value.id ? String(team.value.id) : undefined)
            }
            gutterBottom
          />
          <Select
            id="opponent-dropdown"
            label="Opponent"
            options={teamsOptions}
            value={selectedOpponent}
            onChange={(team: ValueOpt<StatsTeam>) =>
              setParam("opponentTeamId", team.value.id ? String(team.value.id) : undefined)
            }
            gutterBottom
          />
          <DateRangeInput
            id="date-range"
            label="Date"
            value={dateRange}
            onChange={(value: DateRange<any> | NotApplicable) => {
              updateDate(value as DateRange<Date>);
            }}
            gutterBottom
            maxRange={
              hasDateLimit
                ? {
                    weeks: 1,
                  }
                : undefined
            }
            withPortal
          />
        </Filters>
      </Header>
      <div className="bcr-p-3">
        <div style={{ width: isMobile ? "100%" : "500px" }}>
          <SeasonSelect
            userId={user?.employeeUserId}
            year={activeSeason.year?.toString()}
            seasonType={selectedSeason}
            setSeasonType={setSelectedSeason}
            setAllotments={setAllotments}
            pendingLink={true}
          />
        </div>
        {isScheduleLoading ? (
          <div className="bcr-mt-5 d-flex justify-content-center align-items-center w-100 h-100">
            <Loader type="spinner" />
          </div>
        ) : (
          <ScrollContainer offset={24} className="bcr-mt-2">
            {schedule?.map((game: GameDTO) => (
              <Game
                key={game.gamePk}
                gamePk={game.gamePk}
                gameDate={game.gameDate}
                venue={game.venue}
                description={game.seriesDescription}
                teams={game.teams}
                access={game.access}
                cutoff={game.cutoff}
                personalRequests={game.personalRequests}
                blackedOut={game.blackedOut}
                requestCount={gameRequestCounts[game.gamePk]}
                status={game.status}
                promotions={game.atBatPromotions}
              />
            ))}
          </ScrollContainer>
        )}
      </div>
    </div>
  );
};

export default Home;
